import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import Loading from "../../../../common-components/Loading";
import {
  getDFRSummaryAPI,
  getDFRSummaryByDateRangeAPI,
  getVersionsAPI,
} from "../../../../api/reports-api";
import { colsFromKeys, formatRangeLabel } from "../pl-by-program/actualsVsForecastByDateRangeUtils";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
  cellWhiteSpaceStyle,
  dynamicColSize,
} from "../waterfall-eac/waterfallTableUtils";
import {
  calcFilterPos,
  calcOpDropdownPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "../waterfall-eac/filterPopoverUtils";
import {
  fpBase,
  opDropS,
  TextFilterContent,
  NumericFilterContent,
  OpDropdownContent,
} from "../waterfall-eac/tableColumnFilterShared";
import {
  TABLE_FONT,
  TH_BG,
  TH_TEXT,
  TH_BORDER,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
  CELL_FG,
  ACCENT_COLOR,
  TH_HEIGHT,
  TH_BG_BAND,
} from "../../reportTableConfig";
import {
  buildDfrApiFilters,
  getApiFilterKey,
  getGlobalPeriodColumnKeys,
  getVisibleDfrPeriods,
  mapDfrMetricRows,
  resolveDfrMeasureValue,
} from "./dfrFilterUtils";

const FONT = TABLE_FONT;
const TH_TXT = TH_TEXT;
const TH_BDR = TH_BORDER;
const CELL_C = CELL_FG;
const ACCENT = ACCENT_COLOR;
const ROW1_H = TH_HEIGHT;

const PERIODS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
  "total",
];
const PERIOD_LBL = {
  jan: "Jan",
  feb: "Feb",
  mar: "Mar",
  apr: "Apr",
  may: "May",
  jun: "Jun",
  jul: "Jul",
  aug: "Aug",
  sep: "Sep",
  oct: "Oct",
  nov: "Nov",
  dec: "Dec",
  total: "Total",
};
const MONTH_NUM = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const ID_W = 116;
const NAME_W = 112;
/* Frozen text columns that follow Contract ID / Name, in display order. */
const BASE_TEXT_COLS = ["businessType", "businessUnit"];
/* Hidden until ticked in the Columns picker. */
const RANGE_TYPE_COLS = [
  "contractType",
  "awardType",
  "competitionType",
  "popStartDate",
  "popEndDate",
];
const DATE_TEXT_COLS = new Set(["popStartDate", "popEndDate"]);
/* Display (and text-filter) value of a frozen text column; ISO dates render as MM/DD/YYYY. */
const textColValue = (row, key) => {
  const v = row[key];
  if (!v) return "";
  if (DATE_TEXT_COLS.has(key)) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
    return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v);
  }
  return String(v);
};
/* Wider than the default so long headers ("Contract Type", "Competition Type") wrap on word boundaries. */
const TEXT_COL_WIDTHS = { contractType: 120, competitionType: 140 };
const textColW = (key) => TEXT_COL_WIDTHS[key] || 100;
const textColLeft = (cols, i) =>
  cols.slice(0, i).reduce((sum, k) => sum + textColW(k), ID_W + NAME_W);
const COL_W = 84;
/* "Mon YYYY" headers plus sort arrows and filter button need more room than "Mon". */
const RANGE_COL_W = 128;
const ROW2_H = 28;
const NEG = "#D64550";

const DEFAULT_SHOW = new Set(["contractRevenue"]);

const FIXED_COL_LABELS = {
  id: "Contract ID",
  name: "Contract Name",
  businessType: "Business Type",
  businessUnit: "Business Unit",
  contractType: "Contract Type",
  awardType: "Award Type",
  competitionType: "Competition Type",
  popStartDate: "POP Start Date",
  popEndDate: "POP End Date",
};

const OP_ENTRIES = [
  { op: "gt", sym: ">", label: "Greater than" },
  { op: "gte", sym: "≥", label: "At least" },
  { op: "lt", sym: "<", label: "Less than" },
  { op: "lte", sym: "≤", label: "At most" },
  { op: "eq", sym: "=", label: "Equal to" },
  { op: "neq", sym: "≠", label: "Not equal to" },
];

function applyNumFilter(v, f) {
  if (!f?.value) return true;
  const n = Number(v),
    fv = Number(f.value);
  if (isNaN(n) || isNaN(fv)) return false;
  switch (f.op) {
    case "gt":
      return n > fv;
    case "gte":
      return n >= fv;
    case "lt":
      return n < fv;
    case "lte":
      return n <= fv;
    case "eq":
      return n === fv;
    case "neq":
      return n !== fv;
    default:
      return true;
  }
}

function fmt(v, isPct) {
  if (v == null) return "—";
  const n = Number(v);
  if (isPct) return `${(n * 100).toFixed(1)}%`;
  const abs = Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

function FilterIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M3 4h18l-7 8.5V20l-4-2v-5.5L3 4z" />
    </svg>
  );
}
function ColsIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="18" rx="1" />
      <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
  );
}

/* ── Table header styles ── */
const TH_SH = `inset -1px 0 0 ${TH_BDR}, inset 0 -1px 0 ${TH_BDR}`;
const thFixed = {
  position: "sticky",
  zIndex: 12,
  background: TH_BG,
  color: TH_TXT,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.3,
  verticalAlign: "middle",
  boxSizing: "border-box",
  border: "none",
  boxShadow: TH_SH,
  padding: "0 6px",
};
const thGrp = {
  position: "sticky",
  top: 0,
  zIndex: 7,
  background: TH_BG,
  color: TH_TXT,
  height: ROW1_H,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  textAlign: "center",
  verticalAlign: "middle",
  boxSizing: "border-box",
  border: "none",
  boxShadow: `inset 0 -1px 0 ${TH_BDR}`,
  padding: "0 6px",
};
const thMon = (isTotal, isActual = false, colW = COL_W) => ({
  position: "sticky",
  top: ROW1_H,
  zIndex: 7,
  background: isActual ? TH_ACTUAL_BG : TH_BG,
  height: ROW2_H,
  fontSize: 10,
  fontWeight: isTotal || isActual ? 700 : 600,
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  boxSizing: "border-box",
  border: "none",
  boxShadow: TH_SH,
  padding: "0 4px",
  color: isActual ? TH_ACTUAL_TEXT : "rgba(213,218,224,0.75)",
  ...dynamicColSize(colW),
});
const fBtnSt = (a, isActual = false) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  padding: 0,
  borderRadius: 3,
  border: "none",
  cursor: "pointer",
  background: a
    ? isActual
      ? "rgba(0,0,0,0.12)"
      : "rgba(165,180,252,0.18)"
    : "transparent",
  color: isActual ? TH_ACTUAL_TEXT : a ? "#A5B4FC" : TH_TXT,
  opacity: a || isActual ? 1 : 0.5,
  flexShrink: 0,
});

/* ── Cell styles ── */
const tdFixed = (bg, bold) => ({
  position: "sticky",
  left: 0,
  zIndex: 4,
  background: bg,
  boxSizing: "border-box",
  padding: "6px 6px",
  fontSize: 12,
  fontWeight: bold ? 600 : 400,
  color: CELL_C,
  ...cellWhiteSpaceStyle(false),
  verticalAlign: "middle",
  borderBottom: "1px solid #E2E8F0",
  boxShadow: "2px 0 5px -1px rgba(0,0,0,0.07)",
  ...dynamicColSize(ID_W),
  textAlign: "left",
});
const tdName = (bg, bold) => ({
  position: "sticky",
  left: ID_W,
  zIndex: 4,
  background: bg,
  boxSizing: "border-box",
  padding: "6px 6px",
  fontSize: 12,
  fontWeight: bold ? 600 : 400,
  color: CELL_C,
  ...cellWhiteSpaceStyle(false),
  verticalAlign: "middle",
  borderBottom: "1px solid #E2E8F0",
  boxShadow: "2px 0 5px -1px rgba(0,0,0,0.07)",
  ...dynamicColSize(NAME_W),
  textAlign: "left",
});
const tdText = (w, left, bg, bold) => ({
  position: "sticky",
  left,
  zIndex: 4,
  background: bg,
  boxSizing: "border-box",
  padding: "6px 6px",
  fontSize: 12,
  fontWeight: bold ? 600 : 400,
  color: CELL_C,
  ...cellWhiteSpaceStyle(false),
  verticalAlign: "middle",
  borderBottom: "1px solid #E2E8F0",
  boxShadow: "2px 0 5px -1px rgba(0,0,0,0.07)",
  ...dynamicColSize(w),
  textAlign: "left",
});
const tdVal = (v, isPct, isTotal, bg, bold, colW = COL_W) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: "right",
  padding: "6px 4px",
  fontSize: 12,
  fontWeight: bold || isTotal ? 600 : 400,
  color: v != null && Number(v) < 0 ? NEG : CELL_C,
  ...cellWhiteSpaceStyle(true),
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
  ...dynamicColSize(colW),
});

/* ── Card / controls ── */
const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.03)",
  overflow: "hidden",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  marginBottom: 24,
};
const HEAD = {
  padding: "16px 20px 12px",
  borderBottom: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  position: "relative",
  zIndex: 20,
  overflow: "visible",
  flexShrink: 0,
  background: "#fff",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
};
const CARD_BODY = {
  overflow: "hidden",
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
};
const VP = {
  display: "inline-flex",
  alignItems: "center",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  overflow: "hidden",
  background: "#fff",
  height: 34,
};
const VP_L = {
  padding: "0 10px",
  height: "100%",
  display: "flex",
  alignItems: "center",
  background: "rgba(99,102,241,0.08)",
  borderRight: "1px solid #E2E8F0",
  fontSize: 11,
  fontWeight: 700,
  color: "#3730A3",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const VP_S = {
  border: "none",
  outline: "none",
  background: "#fff",
  fontSize: 12,
  color: "#374151",
  padding: "0 8px",
  height: "100%",
  cursor: "pointer",
  fontFamily: FONT,
  minWidth: 120,
};
const cBtn = (a) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  height: 34,
  padding: "0 12px",
  borderRadius: 6,
  border: `1px solid ${a ? "#C7D2FE" : "#E2E8F0"}`,
  background: a ? "#EEF2FF" : "#fff",
  color: a ? "#4F46E5" : "#374151",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: FONT,
});
const cBdg = {
  display: "inline-flex",
  alignItems: "center",
  height: 18,
  padding: "0 7px",
  borderRadius: 10,
  background: ACCENT,
  color: "#fff",
  fontSize: 11,
  fontWeight: 600,
};
const cDrop = {
  position: "absolute",
  top: "calc(100% + 6px)",
  right: 0,
  zIndex: 1200,
  background: "#fff",
  border: "1px solid #E5E7EB",
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  minWidth: 240,
  maxHeight: 420,
  overflowY: "auto",
};

/* ── Pagination ── */
const pagBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA",
  gap: 12,
  flexWrap: "wrap",
};
const pagTxt = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const pSzSel = {
  height: 28,
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 12,
  color: "#374151",
  padding: "0 6px",
  background: "#fff",
  cursor: "pointer",
  fontFamily: FONT,
};
const pBtn = (d) => ({
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  background: "#fff",
  color: d ? "#94A3B8" : "#374151",
  fontSize: 12,
  cursor: d ? "default" : "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
  opacity: d ? 0.35 : 1,
});
const pBtnA = {
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: `1px solid ${TH_BG_BAND}`,
  borderRadius: 6,
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
};
const pDots = {
  fontSize: 12,
  color: "#6B7280",
  padding: "0 4px",
  userSelect: "none",
};

/* Column keys are "<metricKey>-<period>"; metric keys are camelCase (no hyphen) while a
   date-range period is "YYYY-MM", so split on the FIRST hyphen. */
function splitColKey(key) {
  const di = key.indexOf("-");
  return di < 0 ? [key, ""] : [key.slice(0, di), key.slice(di + 1)];
}

/* With `dateRange` this is the "Summary by Date Range" tab: same grid, but the period columns
   are the API's own "YYYY-MM" keys + total instead of jan…dec, and the year filter is unused. */
export default function Summary({ filters, onMeta, dateRange }) {
  const isRange = !!dateRange;
  const rangeKey = isRange ? JSON.stringify(dateRange) : "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("");
  const extraCols = RANGE_TYPE_COLS;
  const [hiddenMetrics, setHiddenMetrics] = useState(() => new Set(extraCols));
  const textCols = useMemo(
    () => [...BASE_TEXT_COLS, ...extraCols.filter((k) => !hiddenMetrics.has(k))],
    [hiddenMetrics], // eslint-disable-line
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const pickerRef = useRef(null);
  const defaultsSet = useRef(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  /* Filter state */
  const [filterText, setFilterText] = useState({
    id: "",
    name: "",
    businessType: "",
    businessUnit: "",
    contractType: "",
    awardType: "",
    competitionType: "",
    popStartDate: "",
    popEndDate: "",
  });
  const [colFilters, setColFilters] = useState({});
  const [sortKeys, setSortKeys] = useState([]);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opPos, setOpPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  useEffect(() => {
    getVersionsAPI(filters?.year || "").then((res) => {
      setVersions(res?.data || []);
      setVersion("");
    });
  }, [filters?.year]); // eslint-disable-line

  const apiFilterKey = useMemo(
    () => getApiFilterKey(filters, buildDfrApiFilters),
    [filters],
  );
  const periodMonthKeys = useMemo(
    () => (isRange ? null : getGlobalPeriodColumnKeys(filters, "Month")),
    [isRange, filters?.monthName, filters?.quarter],
  );
  const rangeColumns = useMemo(() => colsFromKeys(apiData?.columns), [apiData]);
  const visiblePeriods = useMemo(
    () =>
      isRange
        ? rangeColumns.map((c) => c.key)
        : getVisibleDfrPeriods(filters, PERIODS),
    [isRange, rangeColumns, filters?.monthName, filters?.quarter],
  );
  const colW = isRange ? RANGE_COL_W : COL_W;
  const periodLabel = (p) =>
    isRange ? rangeColumns.find((c) => c.key === p)?.label || p : PERIOD_LBL[p];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (isRange
      ? getDFRSummaryByDateRangeAPI(filters, dateRange, version)
      : getDFRSummaryAPI(filters, version)
    )
      .then((res) => {
        if (cancelled) return;
        if (res?.data) {
          setApiData(res.data);
          if (onMeta)
            onMeta({
              actualThroughMonth: res.data.actualThroughMonth,
              fiscalYear: res.data.fiscalYear,
              version,
              // Exact dropdown selection (e.g. "Budget 2027 v1"), sent as params.versionName on download.
              versionName: version
                ? versions.find((v) => v.key === version)?.name || ""
                : "Live EAC",
            });
        } else {
          setError(res?.message || "Failed to load data");
          setApiData(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load data");
          setApiData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiFilterKey, version, rangeKey]); // eslint-disable-line

  /* Default: show only contractRevenue (Revenue) on first load */
  useEffect(() => {
    if (!apiData?.rows?.length || defaultsSet.current) return;
    defaultsSet.current = true;
    setHiddenMetrics(
      new Set([
        ...extraCols,
        ...apiData.rows.map((r) => r.key).filter((k) => !DEFAULT_SHOW.has(k)),
      ]),
    );
  }, [apiData]);

  useEffect(() => {
    setPage(1);
  }, [apiData, filterText, colFilters]);

  /* Close column picker */
  useEffect(() => {
    if (!pickerOpen) return;
    const h = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [pickerOpen]);

  /* Close filter panel */
  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      ) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  /* Close op dropdown */
  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setOpPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    repositionPanels();
  }, [repositionPanels]);

  /* Keep filter popovers anchored below header buttons during page/table scroll */
  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);

  const allMetrics = mapDfrMetricRows(apiData?.rows);
  const contracts = apiData?.contracts || [];
  const grandTotals = apiData?.grandTotals || {};
  const actualThru = Number(apiData?.actualThroughMonth) || 0;
  const fiscalYear = apiData?.fiscalYear || "";
  const actualMonths = apiData?.actualMonths || [];
  const versionName = apiData?.version?.name || "";

  const visibleMetrics = allMetrics.filter((m) => !hiddenMetrics.has(m.key));
  const hiddenCount = hiddenMetrics.size;
  const toggleMetric = (key) =>
    setHiddenMetrics((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const filteredSearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pickable = [
      ...extraCols.map((key) => ({ key, label: FIXED_COL_LABELS[key] })),
      ...allMetrics,
    ];
    return q
      ? pickable.filter((m) => m.label.toLowerCase().includes(q))
      : pickable;
  }, [allMetrics, search, isRange]); // eslint-disable-line

  function hasFilterValue(key) {
    if (key === "id" || key === "name" || textCols.includes(key))
      return !!filterText[key];
    return !!colFilters[key]?.value;
  }

  function toggleFilter(key, btnEl) {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    const btn = btnEl || filterBtnRefs.current[key];
    if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  }

  function toggleOpDropdown(key) {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (!btn) return;
    setOpPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  }

  function renderFilterContent(key) {
    if (key === "id" || key === "name" || textCols.includes(key)) {
      const label = FIXED_COL_LABELS[key];
      return (
        <TextFilterContent
          label={label}
          value={filterText[key]}
          onChange={(v) => setFilterText((p) => ({ ...p, [key]: v }))}
          onClear={() => setFilterText((p) => ({ ...p, [key]: "" }))}
        />
      );
    }
    const [mKey, pKey] = splitColKey(key);
    const meta = allMetrics.find((m) => m.key === mKey);
    const f = colFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={`${meta?.label || mKey} · ${periodLabel(pKey) || pKey}`}
        value={f.value}
        op={f.op}
        onValueChange={(v) =>
          setColFilters((p) => ({ ...p, [key]: { ...f, value: v } }))
        }
        onClear={() =>
          setColFilters((p) => {
            const n = { ...p };
            delete n[key];
            return n;
          })
        }
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => {
          opBtnRefs.current[key] = el;
        }}
      />
    );
  }

  /* Filtered rows */
  const filteredContracts = useMemo(() => {
    return contracts.filter((c) => {
      const idQ = filterText.id.trim().toLowerCase();
      const nameQ = filterText.name.trim().toLowerCase();
      if (
        idQ &&
        !String(c.id || "")
          .toLowerCase()
          .includes(idQ)
      )
        return false;
      if (
        nameQ &&
        !String(c.name || "")
          .toLowerCase()
          .includes(nameQ)
      )
        return false;
      for (const tKey of textCols) {
        const q = filterText[tKey].trim().toLowerCase();
        if (
          q &&
          !textColValue(c, tKey)
            .toLowerCase()
            .includes(q)
        )
          return false;
      }
      for (const [key, f] of Object.entries(colFilters)) {
        if (!f?.value) continue;
        const [fm, fp] = splitColKey(key);
        if (!applyNumFilter(c.measures?.[fm]?.[fp], f))
          return false;
      }
      return true;
    });
  }, [contracts, filterText, colFilters, textCols]);

  const getSortColumnLabel = useCallback(
    (key) => {
      if (FIXED_COL_LABELS[key]) return FIXED_COL_LABELS[key];
      const [mKey, pKey] = splitColKey(key);
      if (pKey) {
        const meta = allMetrics.find((m) => m.key === mKey);
        if (meta) return `${meta.label} · ${periodLabel(pKey) || pKey}`;
      }
      return key;
    },
    [allMetrics, rangeColumns],
  );

  const getSortValue = useCallback((row, key) => {
    if (key === "id") return row.id ?? "";
    if (key === "name") return row.name ?? "";
    if (textCols.includes(key)) return row[key] ?? "";
    const [mKey, pKey] = splitColKey(key);
    if (pKey) {
      const meta = allMetrics.find((m) => m.key === mKey);
      if (meta) {
        return Number(resolveDfrMeasureValue(row.measures, mKey, pKey, meta.isPct, periodMonthKeys)) || 0;
      }
    }
    return "";
  }, [allMetrics, periodMonthKeys, textCols]);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);

  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const sortedContracts = useMemo(() => {
    if (sortKeys.length === 0) return filteredContracts;
    return [...filteredContracts].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredContracts, sortKeys, getSortValue]);

  const hasTableFilters =
    filterText.id.trim() ||
    filterText.name.trim() ||
    Object.values(colFilters).some((f) => f?.value);
  const displayGrandTotals = useMemo(() => {
    const needsRecompute = hasTableFilters || !!periodMonthKeys?.length;
    if (!needsRecompute) return grandTotals;
    const out = {};
    for (const m of allMetrics) {
      out[m.key] = {};
      for (const p of visiblePeriods) {
        if (hasTableFilters) {
          if (m.isPct) continue;
          out[m.key][p] = filteredContracts.reduce(
            (s, c) =>
              s +
              (Number(
                resolveDfrMeasureValue(
                  c.measures,
                  m.key,
                  p,
                  false,
                  periodMonthKeys,
                ),
              ) || 0),
            0,
          );
        } else {
          out[m.key][p] = resolveDfrMeasureValue(
            grandTotals,
            m.key,
            p,
            m.isPct,
            periodMonthKeys,
          );
        }
      }
    }
    return out;
  }, [
    hasTableFilters,
    filteredContracts,
    grandTotals,
    allMetrics,
    periodMonthKeys,
    visiblePeriods,
  ]);

  const TABLE_W =
    ID_W +
    NAME_W +
    textCols.reduce((sum, k) => sum + textColW(k), 0) +
    visibleMetrics.length * visiblePeriods.length * colW;
  const total = sortedContracts.length;
  const totPg = Math.max(1, Math.ceil(total / pageSize));
  const sp = Math.min(page, totPg);
  const paged = sortedContracts.slice((sp - 1) * pageSize, sp * pageSize);
  const rs = total === 0 ? 0 : (sp - 1) * pageSize + 1;
  const re = Math.min(sp * pageSize, total);

  const pageNums = (() => {
    if (totPg <= 7) return Array.from({ length: totPg }, (_, i) => i + 1);
    const s = new Set([1, totPg, sp, sp - 1, sp + 1, sp - 2, sp + 2]);
    return [...s].filter((p) => p >= 1 && p <= totPg).sort((a, b) => a - b);
  })();

  const vOpts = useMemo(
    () => [
      { value: "", label: "Live EAC" },
      ...(versions || [])
        .filter((v) => v.key !== "liveEac")
        .map((v) => ({ value: v.key, label: v.name })),
    ],
    [versions],
  );

  const suffix = [
    isRange && apiData
      ? formatRangeLabel(apiData.startMonth, apiData.startYear, apiData.endMonth, apiData.endYear)
      : "",
    fiscalYear ? `FY${fiscalYear}` : "",
    // Show the version picked in the dropdown (not the API's own name for it, which can differ,
    // e.g. "Budget 2026" returned for "Budget 2027 v1").
    version
      ? vOpts.find((o) => o.value === version)?.label || versionName
      : versionName && versionName !== "Live EAC"
        ? versionName
        : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div style={{ fontFamily: FONT, paddingBottom: 24 }}>
      <div style={CARD}>
        <div style={HEAD}>
          <div>
            <h2
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#111827",
                letterSpacing: "-0.01em",
                fontFamily: FONT,
                margin: 0,
              }}
            >
              Current Month Actuals + Live Forecast
              {suffix ? ` — ${suffix}` : ""}
            </h2>
            {sortKeys.length > 0 && (
              <div
                style={{
                  fontSize: 12,
                  color: "#6B7280",
                  marginTop: 4,
                  fontFamily: FONT,
                }}
              >
                <span style={{ color: ACCENT, fontWeight: 500 }}>
                  Sorted by{" "}
                  {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
                </span>
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <div style={VP}>
              <span style={VP_L}>Version</span>
              <select
                style={VP_S}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              >
                {vOpts.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div
              ref={pickerRef}
              style={{ position: "relative", flexShrink: 0 }}
            >
              <button
                type="button"
                style={cBtn(pickerOpen || hiddenCount > 0)}
                onClick={() => setPickerOpen((p) => !p)}
              >
                <ColsIcon />
                Columns
                {hiddenCount > 0 && (
                  <span style={cBdg}>{hiddenCount} Hidden</span>
                )}
              </button>
              {pickerOpen && (
                <div style={cDrop}>
                  <div
                    style={{
                      padding: "8px 14px",
                      background: "#F8FAFC",
                      borderBottom: "1px solid #E2E8F0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#111827",
                        fontFamily: FONT,
                      }}
                    >
                      Visible Metrics
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#6B7280",
                        fontFamily: FONT,
                      }}
                    >
                      {allMetrics.length + extraCols.length - hiddenCount} of{" "}
                      {allMetrics.length + extraCols.length}
                    </span>
                  </div>
                  <input
                    type="search"
                    placeholder="Search metrics…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      display: "block",
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "7px 10px 7px 32px",
                      border: "none",
                      borderBottom: "1px solid #F1F5F9",
                      fontSize: 12,
                      fontFamily: FONT,
                      outline: "none",
                      background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E") no-repeat 10px center`,
                    }}
                  />
                  <div
                    style={{
                      maxHeight: 260,
                      overflowY: "auto",
                      padding: "4px 0 8px",
                    }}
                  >
                    {filteredSearch.length === 0 ? (
                      <div
                        style={{
                          padding: "14px",
                          fontSize: 12,
                          color: "#94A3B8",
                          textAlign: "center",
                          fontFamily: FONT,
                        }}
                      >
                        No matching metrics
                      </div>
                    ) : (
                      filteredSearch.map((m) => (
                        <label
                          key={m.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "7px 14px",
                            cursor: "pointer",
                            fontSize: 12,
                            color: "#374151",
                            fontFamily: FONT,
                          }}
                        >
                          <input
                            type="checkbox"
                            style={{
                              accentColor: ACCENT,
                              cursor: "pointer",
                              width: 14,
                              height: 14,
                              flexShrink: 0,
                            }}
                            checked={!hiddenMetrics.has(m.key)}
                            onChange={() => toggleMetric(m.key)}
                          />
                          {m.label}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={CARD_BODY}>
          {loading && <Loading />}
          {!loading && error && (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: "#D64550",
                fontSize: 13,
                fontFamily: FONT,
              }}
            >
              {error}
            </div>
          )}
          {!loading && !error && contracts.length === 0 && (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: "#6B7280",
                fontSize: 13,
                fontFamily: FONT,
              }}
            >
              No data for the selected filters.
            </div>
          )}

          {!loading && !error && contracts.length > 0 && (
            <div
              style={{
                overflowX: "auto",
                overflowY: "hidden",
                width: "100%",
                maxWidth: "100%",
              }}
              onScroll={handleTableScroll}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  borderSpacing: 0,
                  tableLayout: "fixed",
                  fontFamily: FONT,
                  width: TABLE_W,
                  minWidth: TABLE_W,
                }}
              >
                <colgroup>
                  <col style={dynamicColSize(ID_W)} />
                  <col style={dynamicColSize(NAME_W)} />
                  {textCols.map((key) => (
                    <col key={key} style={dynamicColSize(textColW(key))} />
                  ))}
                  {visibleMetrics.flatMap((m) =>
                    visiblePeriods.map((p) => (
                      <col
                        key={`${m.key}-${p}`}
                        style={dynamicColSize(colW)}
                      />
                    )),
                  )}
                </colgroup>
                <thead>
                  <tr>
                    <th
                      rowSpan={2}
                      style={{
                        ...thFixed,
                        left: 0,
                        ...dynamicColSize(ID_W),
                        top: 0,
                        height: ROW1_H + ROW2_H,
                        cursor: "pointer",
                      }}
                      onClick={onThClick("id")}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "space-between",
                          gap: 4,
                          minWidth: 0,
                        }}
                      >
                        <WaterfallThLabel
                          label={FIXED_COL_LABELS.id}
                          colKey="id"
                          sortKeys={sortKeys}
                          getLabel={getSortColumnLabel}
                          highlight={openFilterKey === "id"}
                          wrap
                        />
                        <button
                          ref={(el) => {
                            filterBtnRefs.current["id"] = el;
                          }}
                          data-filter-btn
                          style={fBtnSt(
                            openFilterKey === "id" || hasFilterValue("id"),
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFilter("id", e.currentTarget);
                          }}
                        >
                          <FilterIcon />
                        </button>
                      </div>
                    </th>
                    <th
                      rowSpan={2}
                      style={{
                        ...thFixed,
                        left: ID_W,
                        ...dynamicColSize(NAME_W),
                        top: 0,
                        height: ROW1_H + ROW2_H,
                        cursor: "pointer",
                      }}
                      onClick={onThClick("name")}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "space-between",
                          gap: 4,
                          minWidth: 0,
                        }}
                      >
                        <WaterfallThLabel
                          label={FIXED_COL_LABELS.name}
                          colKey="name"
                          sortKeys={sortKeys}
                          getLabel={getSortColumnLabel}
                          highlight={openFilterKey === "name"}
                          wrap
                        />
                        <button
                          ref={(el) => {
                            filterBtnRefs.current["name"] = el;
                          }}
                          data-filter-btn
                          style={fBtnSt(
                            openFilterKey === "name" || hasFilterValue("name"),
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFilter("name", e.currentTarget);
                          }}
                        >
                          <FilterIcon />
                        </button>
                      </div>
                    </th>
                    {textCols.map((key, i) => (
                      <th
                        key={key}
                        rowSpan={2}
                        style={{
                          ...thFixed,
                          left: textColLeft(textCols, i),
                          ...dynamicColSize(textColW(key)),
                          top: 0,
                          height: ROW1_H + ROW2_H,
                          cursor: "pointer",
                        }}
                        onClick={onThClick(key)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            gap: 4,
                            minWidth: 0,
                          }}
                        >
                          <WaterfallThLabel
                            label={FIXED_COL_LABELS[key]}
                            colKey={key}
                            sortKeys={sortKeys}
                            getLabel={getSortColumnLabel}
                            highlight={openFilterKey === key}
                            wrap
                          />
                          <button
                            ref={(el) => {
                              filterBtnRefs.current[key] = el;
                            }}
                            data-filter-btn
                            style={fBtnSt(
                              openFilterKey === key || hasFilterValue(key),
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFilter(key, e.currentTarget);
                            }}
                          >
                            <FilterIcon />
                          </button>
                        </div>
                      </th>
                    ))}
                    {visibleMetrics.map((m) => (
                      <th
                        key={m.key}
                        colSpan={visiblePeriods.length}
                        style={thGrp}
                      >
                        {m.label}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {visibleMetrics.flatMap((m) =>
                      visiblePeriods.map((p) => {
                        const fKey = `${m.key}-${p}`;
                        const active =
                          openFilterKey === fKey || hasFilterValue(fKey);
                        const isActual =
                          p !== "total" &&
                          (isRange
                            ? actualMonths.includes(p)
                            : actualThru > 0 && MONTH_NUM[p] <= actualThru);
                        return (
                          <th
                            key={fKey}
                            style={{ ...thMon(p === "total", isActual, colW), cursor: "pointer" }}
                            onClick={onThClick(fKey)}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 3,
                              }}
                            >
                              <WaterfallThLabel
                                label={periodLabel(p)}
                                colKey={fKey}
                                sortKeys={sortKeys}
                                getLabel={getSortColumnLabel}
                                highlight={active}
                                align="right"
                                textColor={isActual && !active ? TH_ACTUAL_TEXT : undefined}
                                tone={isActual ? "actual" : "default"}
                              />
                              <button
                                ref={(el) => {
                                  filterBtnRefs.current[fKey] = el;
                                }}
                                data-filter-btn
                                style={fBtnSt(active, isActual)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFilter(fKey, e.currentTarget);
                                }}
                              >
                                <FilterIcon />
                              </button>
                            </div>
                          </th>
                        );
                      }),
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      style={{ ...tdFixed("#F0FFF4", true), color: "#111827" }}
                    >
                      Total
                    </td>
                    <td style={{ ...tdName("#F0FFF4", false) }} />
                    {textCols.map((key, i) => (
                      <td
                        key={key}
                        style={tdText(textColW(key), textColLeft(textCols, i), "#F0FFF4", false)}
                      />
                    ))}
                    {visibleMetrics.flatMap((m) =>
                      visiblePeriods.map((p) => {
                        const v = displayGrandTotals[m.key]?.[p];
                        return (
                          <td
                            key={`gt-${m.key}-${p}`}
                            style={{
                              ...tdVal(
                                v,
                                m.isPct,
                                p === "total",
                                "#F0FFF4",
                                true,
                                colW,
                              ),
                              color:
                                v != null && Number(v) < 0 ? NEG : "#111827",
                            }}
                          >
                            {fmt(v, m.isPct)}
                          </td>
                        );
                      }),
                    )}
                  </tr>
                  {paged.map((c, ci) => {
                    const bg = ci % 2 === 0 ? "#fff" : "#F8FAFC";
                    return (
                      <tr key={c.id}>
                        <td style={tdFixed(bg, false)}>{c.id}</td>
                        <td style={tdName(bg, false)}>{c.name || c.id}</td>
                        {textCols.map((key, i) => (
                          <td
                            key={key}
                            style={tdText(textColW(key), textColLeft(textCols, i), bg, false)}
                          >
                            {textColValue(c, key)}
                          </td>
                        ))}
                        {visibleMetrics.flatMap((m) =>
                          visiblePeriods.map((p) => {
                            const v = resolveDfrMeasureValue(
                              c.measures,
                              m.key,
                              p,
                              m.isPct,
                              periodMonthKeys,
                            );
                            return (
                              <td
                                key={`${c.id}-${m.key}-${p}`}
                                style={tdVal(
                                  v,
                                  m.isPct,
                                  p === "total",
                                  bg,
                                  false,
                                  colW,
                                )}
                              >
                                {fmt(v, m.isPct)}
                              </td>
                            );
                          }),
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && total > 0 && (
            <div style={pagBar}>
              <span style={pagTxt}>{`${rs}–${re} of ${total} contracts`}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={pagTxt}>Per page</span>
                <select
                  style={pSzSel}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <button
                    style={pBtn(sp === 1)}
                    disabled={sp === 1}
                    onClick={() => setPage(1)}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M9 10L6 7l3-3M5 10V4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    style={pBtn(sp === 1)}
                    disabled={sp === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M8.5 10.5L5 7l3.5-3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {pageNums.map((p, i) => (
                    <React.Fragment key={p}>
                      {i > 0 && pageNums[i - 1] < p - 1 && (
                        <span style={pDots}>…</span>
                      )}
                      <button
                        style={p === sp ? pBtnA : pBtn(false)}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                  <button
                    style={pBtn(sp === totPg)}
                    disabled={sp === totPg}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M5.5 3.5L9 7l-3.5 3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    style={pBtn(sp === totPg)}
                    disabled={sp === totPg}
                    onClick={() => setPage(totPg)}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M5 10l3-3-3-3M9 10V4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter panel portal */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{ ...fpBase, top: filterPos.top, left: filterPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* Operator dropdown portal */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey] || {}).op || "gt"}
            onSelect={(op) => {
              setColFilters((p) => ({
                ...p,
                [openOpKey]: { ...(p[openOpKey] || { value: "" }), op },
              }));
              setOpenOpKey(null);
            }}
            style={{ top: opPos.top, left: opPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}
